import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import HistoryIcon from '@mui/icons-material/History'
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic'
import LogoutIcon from '@mui/icons-material/Logout'
import PeopleIcon from '@mui/icons-material/People'
import PersonIcon from '@mui/icons-material/Person'
import QueueMusicIcon from '@mui/icons-material/QueueMusic'
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import type { ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { useAuth } from '../auth/useAuth'
import { isActive } from '../queue/useJobs'
import { useJobsContext } from '../queue/useJobsContext'
import { SURFACE_RAISED, SURFACE_SELECTED } from '../theme'
import GpuControl from './GpuControl'
import NewVersionBar from './NewVersionBar'
import SingaLogo from './SingaLogo'

const WIDTH = 232

/**
 * One row style for every sidebar entry, so the nav and the footer cannot drift
 * apart. The selected state is a flat fill rather than a brand tint -- the
 * design system is explicit about that.
 */
const NAV_ROW = {
  borderRadius: 1,
  mb: 0.5,
  py: 0.9,
  '&.Mui-selected': { bgcolor: SURFACE_SELECTED },
  '&.Mui-selected:hover': { bgcolor: SURFACE_SELECTED },
} as const

interface NavItem {
  to: string
  label: string
  icon: ReactNode
  sysadminOnly?: boolean
}

/** Named so the unfinished-orders badge can find its row without matching a label. */
const QUEUE = '/queue'

const NAV: NavItem[] = [
  { to: '/order', label: 'Order', icon: <LibraryMusicIcon /> },
  { to: QUEUE, label: 'Queue', icon: <QueueMusicIcon /> },
  { to: '/users', label: 'Users', icon: <PeopleIcon />, sysadminOnly: true },
  { to: '/audit', label: 'Audit Log', icon: <HistoryIcon />, sysadminOnly: true },
]

// Not in NAV: My account sits in the footer group with Sign out, below the
// name and role it belongs to, rather than among the screens for doing the work.
const ACCOUNT = '/account'

export default function AppShell({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth()
  const { jobs } = useJobsContext()
  /**
   * Orders still to finish. Terminal-but-failed does not count: those are done,
   * and a badge that never clears stops meaning anything. Undefined until the
   * first poll answers, so nothing flashes a zero on load.
   */
  const unfinished = jobs?.filter(isActive).length ?? 0
  const location = useLocation()
  const isSysadmin = user?.role === 'sysadmin'
  const items = NAV.filter((item) => !item.sysadminOnly || isSysadmin)

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: WIDTH,
            boxSizing: 'border-box',
            borderRight: 1,
            borderColor: 'divider',
          },
        }}
      >
        {/* Brand above product: this is one Singa tool among several, and the
            wordmark alone would not say which one you have open. */}
        <Box sx={{ px: 2.5, pt: 3, pb: 2.5 }}>
          <SingaLogo height={24} />
          <Typography
            component="h1"
            sx={{
              mt: 0.75,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'text.secondary',
            }}
          >
            taikapeili
          </Typography>
        </Box>

        <List sx={{ flexGrow: 1, py: 0, px: 1 }}>
          {items.map((item) => {
            const active = location.pathname.startsWith(item.to)
            return (
              <ListItemButton
                key={item.to}
                component={NavLink}
                to={item.to}
                selected={active}
                sx={NAV_ROW}
              >
                {/* Green marks where you are. It is the only place the accent
                    appears in the sidebar, which is what makes it readable. */}
                <ListItemIcon
                  sx={{ minWidth: 34, color: active ? 'primary.main' : 'text.secondary' }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: active ? 600 : 400,
                    color: active ? 'text.primary' : 'text.secondary',
                  }}
                />
                {item.to === QUEUE && unfinished > 0 && (
                  <Chip
                    size="small"
                    color="primary"
                    label={unfinished}
                    // Green because it means "work in progress", which is what
                    // the design system uses the brand green for elsewhere --
                    // the ongoing indicator, the running toggle.
                    sx={{ height: 20, minWidth: 20, fontSize: 11, fontWeight: 600 }}
                  />
                )}
              </ListItemButton>
            )
          })}
        </List>

        <Divider sx={{ mx: 2 }} />
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2.5, py: 2 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: SURFACE_RAISED, color: 'text.secondary' }}>
            <PersonIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body1" fontWeight={500} noWrap>
              {user?.username}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user?.role}
            </Typography>
          </Box>
        </Stack>
        {/* Inside a List, not loose in the Drawer. ListItemButton sets
            flex-grow: 1 so it fills a ListItem horizontally; as a direct child
            of the Drawer's flex column that made it stretch down the whole
            remaining height instead. */}
        <List sx={{ py: 0, pb: 1.5, px: 1 }}>
          <ListItemButton
            component={NavLink}
            to={ACCOUNT}
            selected={location.pathname.startsWith(ACCOUNT)}
            sx={NAV_ROW}
          >
            <ListItemIcon sx={{ minWidth: 34, color: 'text.secondary' }}>
              <AccountCircleIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText primary="My Account" primaryTypographyProps={{ fontSize: 13 }} />
          </ListItemButton>
          <ListItemButton onClick={() => void signOut()} sx={NAV_ROW}>
            <ListItemIcon sx={{ minWidth: 34, color: 'text.secondary' }}>
              <LogoutIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText primary="Sign Out" primaryTypographyProps={{ fontSize: 13 }} />
          </ListItemButton>
        </List>
      </Drawer>

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar
          position="sticky"
          color="transparent"
          elevation={0}
          sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default' }}
        >
          <Toolbar sx={{ gap: 2, justifyContent: 'flex-end' }}>
            <Tooltip title="Powering the GPU on starts hourly billing.">
              <span>
                <GpuControl />
              </span>
            </Tooltip>
          </Toolbar>
        </AppBar>

        <NewVersionBar />

        <Box component="main" sx={{ flexGrow: 1, p: 3, minWidth: 0 }}>
          {children}
        </Box>
      </Box>
    </Box>
  )
}
